import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, Building, Check, Loader2, ArrowLeft, Upload, FileImage } from "lucide-react";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../components/common/Brutal";
import { Logo } from "../components/common/Logo";
import { api, peso } from "../lib/api";

const METHOD_META = {
  gcash: { label: "GCash", icon: Smartphone, color: "#0070E0" },
  maya: { label: "Maya", icon: Smartphone, color: "#00C172" },
  bank_transfer: { label: "Bank Transfer", icon: Building, color: "#E5247C" },
};

export default function Payment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [channels, setChannels] = useState([]);
  const [method, setMethod] = useState("");
  const [channelId, setChannelId] = useState("");
  const [form, setForm] = useState({ amount: "", transaction_date: "", reference_number: "", screenshot_base64: "" });
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${id}`).then(({ data }) => {
      setBooking(data);
      setForm((f) => ({ ...f, amount: String(data.total_amount) }));
    });
    api.get("/payment-channels").then(({ data }) => setChannels(data));
  }, [id]);

  const availableMethods = [...new Set(channels.map((c) => c.method))];
  const channelsForMethod = channels.filter((c) => c.method === method);
  const selectedChannel = channels.find((c) => c.id === channelId);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, screenshot_base64: reader.result }));
    reader.readAsDataURL(f);
  };

  const reserve = async () => {
    if (!method) return toast.error("Select payment method");
    if (!channelId) return toast.error("Select a channel");
    if (!form.amount || !form.transaction_date || !form.reference_number || !form.screenshot_base64)
      return toast.error("Fill all payment details and upload screenshot");
    setProcessing(true);
    try {
      await api.post(`/bookings/${id}/reserve`, {
        method, channel_id: channelId,
        amount: parseFloat(form.amount),
        transaction_date: form.transaction_date,
        reference_number: form.reference_number,
        screenshot_base64: form.screenshot_base64,
      });
      setSuccess(true);
      toast.success("Reservation submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setProcessing(false); }
  };

  if (!booking) return <div className="p-10 text-center">Loading…</div>;

  if (success) {
    return (
      <div className="min-h-screen bg-[#FFF4E6] grid place-items-center px-4 py-10">
        <BrutalCard className="max-w-lg w-full p-8 text-center" data-testid="payment-success">
          <div className="bg-[#10B981] kf-border h-16 w-16 mx-auto grid place-items-center">
            <Check className="h-9 w-9 text-white" strokeWidth={3} />
          </div>
          <h1 className="display text-3xl mt-5">Booking Reservation and Payment Received!</h1>
          <p className="mt-2 text-[#6B4E9E]">
            Your KAOGMA GROUNDS CAMPSITE slots are locked. You will receive an email for the confirmation of your reservation. QR codes for campers and car will be available in your dashboard once payment is confirmed.
          </p>
          <div className="kf-border bg-[#FFB400] p-4 mt-6">
            <div className="text-xs font-bold uppercase tracking-widest">Booking ID</div>
            <div className="display text-3xl mt-1">{booking.booking_id}</div>
            <div className="text-xs uppercase tracking-widest mt-1">Passcode: <b>{booking.passcode}</b></div>
            <div className="text-[10px] mt-2 text-[#3D1E6D]/70">Save these to retrieve your booking later.</div>
          </div>
          <BrutalButton variant="primary" className="w-full mt-6" onClick={() => navigate("/camper")} data-testid="payment-go-dashboard">
            Go to Dashboard
          </BrutalButton>
        </BrutalCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF4E6]">
      <header className="border-b-2 border-[#3D1E6D] bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Logo />
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#3D1E6D] hover:text-[#E5247C]">
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <SectionTitle kicker="Payment" title="Choose how you’ll pay" />

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <BrutalCard className="p-6">
              <h3 className="display text-2xl mb-4">Payment method</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {availableMethods.map((m) => {
                  const meta = METHOD_META[m];
                  const active = method === m;
                  return (
                    <button key={m} onClick={() => { setMethod(m); setChannelId(""); }} data-testid={`pay-method-${m}`}
                      className={`text-left p-4 border-2 border-[#3D1E6D] flex items-center gap-3 ${active ? "bg-[#FFB400] shadow-[4px_4px_0_#3D1E6D]" : "bg-white"}`}>
                      <div className="h-10 w-10 grid place-items-center kf-border" style={{ background: meta.color }}>
                        <meta.icon className="h-5 w-5 text-white" strokeWidth={2.75} />
                      </div>
                      <div className="font-bold">{meta.label}</div>
                      {active && <Check className="h-4 w-4 ml-auto text-[#E5247C]" />}
                    </button>
                  );
                })}
              </div>
            </BrutalCard>

            {method && (
              <BrutalCard className="p-6">
                <h3 className="display text-2xl mb-4">Payment details</h3>
                <div className="space-y-3">
                  <div>
                    <BrutalLabel>Send payment to</BrutalLabel>
                    <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={channelId} onChange={(e) => setChannelId(e.target.value)} data-testid="pay-channel-select">
                      <option value="">Select a channel…</option>
                      {channelsForMethod.map((c) => (
                        <option key={c.id} value={c.id}>{c.account_name} {c.mobile && `· ${c.mobile}`} {c.account_number && `· ${c.account_number}`}</option>
                      ))}
                    </select>
                  </div>
                  {selectedChannel && (
                    <div className="kf-border bg-[#FFF4E6] p-4 text-sm">
                      <Badge color="magenta">{selectedChannel.method.toUpperCase()}</Badge>
                      <div className="font-bold mt-2">{selectedChannel.account_name}</div>
                      {selectedChannel.mobile && <div>Mobile: <b>{selectedChannel.mobile}</b></div>}
                      {selectedChannel.account_number && <div>Account: <b>{selectedChannel.account_number}</b> ({selectedChannel.account_type})</div>}
                      <div className="text-xs text-[#6B4E9E] mt-2">Send your payment to the details above, then fill in the form below.</div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <BrutalLabel>Amount Paid (₱)</BrutalLabel>
                      <BrutalInput type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} data-testid="pay-amount" />
                    </div>
                    <div>
                      <BrutalLabel>Date of Transaction</BrutalLabel>
                      <BrutalInput type="date" value={form.transaction_date} onChange={(e) => setForm({...form, transaction_date: e.target.value})} data-testid="pay-date" />
                    </div>
                    <div>
                      <BrutalLabel>Reference Number</BrutalLabel>
                      <BrutalInput value={form.reference_number} onChange={(e) => setForm({...form, reference_number: e.target.value})} data-testid="pay-ref" />
                    </div>
                  </div>
                  <div>
                    <BrutalLabel>Upload screenshot of payment transfer</BrutalLabel>
                    <label className="kf-border bg-white p-4 flex items-center gap-3 cursor-pointer hover:bg-[#FFF4E6]" data-testid="pay-upload">
                      <Upload className="h-5 w-5" strokeWidth={2.75} />
                      <span className="font-bold text-sm">{form.screenshot_base64 ? "Screenshot attached" : "Click to upload (max 4MB)"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                      {form.screenshot_base64 && <FileImage className="ml-auto h-5 w-5 text-[#10B981]" />}
                    </label>
                    {form.screenshot_base64 && <img src={form.screenshot_base64} alt="screenshot" className="mt-2 max-h-32 kf-border" />}
                  </div>
                </div>
              </BrutalCard>
            )}
          </div>

          <div className="space-y-4">
            <BrutalCard className="p-6">
              <h3 className="display text-2xl">Summary</h3>
              <div className="mt-3 text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-[#6B4E9E]">Zone</span><span className="font-bold">{booking.zone_name}</span></div>
                <div className="flex justify-between"><span className="text-[#6B4E9E]">Type</span><Badge color="magenta">{booking.camping_type.toUpperCase()}</Badge></div>
                <div className="flex justify-between"><span className="text-[#6B4E9E]">Dates</span><span className="font-bold">{booking.check_in} → {booking.check_out}</span></div>
                <div className="flex justify-between"><span className="text-[#6B4E9E]">Nights</span><span className="font-bold">{booking.nights}</span></div>
                <div className="flex justify-between"><span className="text-[#6B4E9E]">Persons</span><span className="font-bold">{booking.persons.length}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t-2 border-dashed border-[#3D1E6D]/30">
                <div className="flex justify-between items-end">
                  <span className="text-xs font-bold uppercase tracking-widest text-[#6B4E9E]">Total</span>
                  <span className="display text-3xl">{peso(booking.total_amount)}</span>
                </div>
              </div>
            </BrutalCard>
            <BrutalButton variant="primary" className="w-full text-base py-4" onClick={reserve} disabled={processing} data-testid="reserve-now-btn">
              {processing ? <><Loader2 className="h-5 w-5 animate-spin" /> Processing…</> : <>RESERVE NOW</>}
            </BrutalButton>
          </div>
        </div>
      </div>
    </div>
  );
}
